import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Home as HomeIcon, Users, Shield, TrendingUp, ArrowRight } from 'lucide-react';
import { getProperties, getSiteSettings } from '../services/api';
import SEO from '../components/SEO';

const Home = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper function to construct image URLs
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    return imageUrl.startsWith('http') ? imageUrl : `${BACKEND_URL}${imageUrl}`;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [props, siteSettings] = await Promise.all([
          getProperties('available'),
          getSiteSettings()
        ]);
        setFeaturedProperties(props.slice(0, 3));
        setSettings(siteSettings);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO 
        title="Home"
        description="Find your perfect rental home in Lynchburg, VA with Otter River Properties. Quality property management services, transparent policies, and exceptional homes."
        keywords="property management Lynchburg VA, rental homes Lynchburg, apartments for rent Lynchburg, houses for rent Lynchburg VA, Lynchburg rentals, property rentals Virginia"
        type="website"
      />
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden" data-darkreader-ignore>
        <div className="absolute inset-0">
          <img
            src={settings?.home_hero_image || "https://images.unsplash.com/photo-1628624747186-a941c476b7ef"}
            alt="Beautiful home in Lynchburg"
            className="w-full h-full"
            style={{ objectFit: settings?.home_hero_image_fit || 'cover' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/50"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
              {settings?.home_hero_title || "Find Your Perfect Home in Lynchburg"}
            </h1>
            <p className="text-xl text-gray-200 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
              {settings?.home_hero_subtitle || "Quality property management services you can trust. We connect great tenants with exceptional homes."}
            </p>
            <div className="flex flex-wrap gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
              <Button asChild size="lg" className="bg-otter-green hover:bg-otter-green-600 text-white text-lg px-8">
                <Link to="/listings">View Listings</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 text-white border-white hover:bg-white/20 text-lg px-8">
                <Link to="/about">Learn More</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      {settings?.why_choose_enabled && (
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {settings?.why_choose_title || "Why Choose Otter River Properties?"}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {settings?.why_choose_subtitle || "Over 15 years of dedicated service to the Lynchburg community"}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {settings?.feature_1_enabled && (
              <Card className="border-2 hover:border-otter-green transition-all duration-300 hover:shadow-lg dark:bg-gray-900 dark:border-gray-800">
                <CardContent className="pt-8 pb-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-otter-green-100 dark:bg-otter-green-800/30 flex items-center justify-center">
                    <HomeIcon className="h-8 w-8 text-otter-green dark:text-otter-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 dark:text-white">
                    {settings?.feature_1_title || "Quality Homes"}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {settings?.feature_1_description || "Well-maintained properties in desirable Lynchburg neighborhoods"}
                  </p>
                </CardContent>
              </Card>
            )}
            {settings?.feature_2_enabled && (
              <Card className="border-2 hover:border-otter-green transition-all duration-300 hover:shadow-lg dark:bg-gray-900 dark:border-gray-800">
                <CardContent className="pt-8 pb-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-otter-green-100 dark:bg-otter-green-800/30 flex items-center justify-center">
                    <Users className="h-8 w-8 text-otter-green dark:text-otter-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 dark:text-white">
                    {settings?.feature_2_title || "Expert Team"}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {settings?.feature_2_description || "Experienced professionals dedicated to your satisfaction"}
                  </p>
                </CardContent>
              </Card>
            )}
            {settings?.feature_3_enabled && (
              <Card className="border-2 hover:border-otter-green transition-all duration-300 hover:shadow-lg dark:bg-gray-900 dark:border-gray-800">
                <CardContent className="pt-8 pb-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-otter-green-100 dark:bg-otter-green-800/30 flex items-center justify-center">
                    <Shield className="h-8 w-8 text-otter-green dark:text-otter-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 dark:text-white">
                    {settings?.feature_3_title || "Trusted Service"}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {settings?.feature_3_description || "Transparent processes and reliable property management"}
                  </p>
                </CardContent>
              </Card>
            )}
            {settings?.feature_4_enabled && (
              <Card className="border-2 hover:border-otter-green transition-all duration-300 hover:shadow-lg dark:bg-gray-900 dark:border-gray-800">
                <CardContent className="pt-8 pb-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-otter-green-100 dark:bg-otter-green-800/30 flex items-center justify-center">
                    <TrendingUp className="h-8 w-8 text-otter-green dark:text-otter-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 dark:text-white">
                    {settings?.feature_4_title || "Local Expertise"}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {settings?.feature_4_description || "Deep understanding of the Lynchburg rental market"}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Featured Properties */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Featured Properties</h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">Discover your next home</p>
            </div>
            <Button asChild variant="outline" className="hidden md:flex items-center gap-2">
              <Link to="/listings">
                View All
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">Loading properties...</p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProperties.map((property) => (
              <Card key={property.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 dark:bg-gray-800 dark:border-gray-700">
                <Link to={`/property/${property.slug}`}>
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={getImageUrl(property.image)}
                      alt={property.title}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-otter-green text-white text-sm font-semibold rounded-full">
                        Available
                      </span>
                    </div>
                  </div>
                </Link>
                <CardContent className="p-6">
                  <Link to={`/property/${property.slug}`}>
                    <h3 className="text-xl font-semibold mb-2 dark:text-white hover:text-otter-green dark:hover:text-otter-green-300 transition-colors">{property.title}</h3>
                  </Link>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">{property.address}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-otter-green dark:text-otter-green-400">${property.price}/mo</span>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <span>{property.bedrooms} Beds</span>
                    <span>•</span>
                    <span>{property.bathrooms} Baths</span>
                    <span>•</span>
                    <span>{property.sqft} sqft</span>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/property/${property.slug}`} className="flex-1">
                      <Button className="w-full bg-otter-green hover:bg-otter-green-600 text-white">
                        View Details
                      </Button>
                    </Link>
                    {property.applicationLink && (
                      <a 
                        href={property.applicationLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button className="w-full bg-otter-green hover:bg-otter-green text-white border-2 border-otter-green">
                          Apply Now
                        </Button>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
          <div className="text-center mt-8 md:hidden">
            <Button asChild variant="outline">
              <Link to="/listings">View All Listings</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-otter-green">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Find Your New Home?</h2>
          <p className="text-xl text-blue-50 mb-8 max-w-2xl mx-auto">
            Browse our available properties or learn about our tenant qualification process
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-otter-green hover:bg-gray-100 text-lg px-8">
              <Link to="/listings">Browse Listings</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
              <Link to="/qualifications">View Qualifications</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;